import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { firestoreService } from '../firebase/firestore';
import { SEASONAL_THEMES } from '../theme/seasonalThemes';
import { useTheme } from './ThemeContext';

const SeasonalThemeContext = createContext();

export const useSeasonalTheme = () => {
  const context = useContext(SeasonalThemeContext);
  if (!context) {
    throw new Error('useSeasonalTheme must be used within a SeasonalThemeProvider');
  }
  return context;
};

export const SeasonalThemeProvider = ({ children }) => {
  const [activeThemeId, setActiveThemeId] = useState(null);
  const [avatars, setAvatars] = useState({});
  const [bannerText, setBannerText] = useState({ en: '', vi: '' });
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  const activeTheme = activeThemeId ? SEASONAL_THEMES[activeThemeId] || null : null;
  const avatarUrl = activeThemeId ? (avatars[activeThemeId] || null) : null;

  // Load from Firestore
  useEffect(() => {
    const load = async () => {
      try {
        const doc = await firestoreService.getById('appSettings', 'seasonalTheme');
        if (doc) {
          if (doc.themeId && SEASONAL_THEMES[doc.themeId]) {
            setActiveThemeId(doc.themeId);
          }
          if (doc.avatars) setAvatars(doc.avatars);
          if (doc.bannerText) setBannerText(doc.bannerText);
        }
      } catch (err) {
        console.error('Error loading seasonal theme:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Apply CSS variable overrides
  useEffect(() => {
    const root = document.documentElement;
    if (!activeTheme) {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-light');
      root.style.removeProperty('--brand-shadow');
      root.style.removeProperty('--brand-shadow-strong');
      root.style.removeProperty('--brand-focus');
      return;
    }
    const colors = isDark ? activeTheme.colors.dark : activeTheme.colors.light;
    root.style.setProperty('--brand-primary', colors.brandPrimary);
    root.style.setProperty('--brand-secondary', colors.brandSecondary);
    root.style.setProperty('--brand-light', colors.brandLight);
    root.style.setProperty('--brand-shadow', colors.brandShadow);
    root.style.setProperty('--brand-shadow-strong', colors.brandShadowStrong);
    root.style.setProperty('--brand-focus', colors.brandFocus);
    return () => {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-light');
      root.style.removeProperty('--brand-shadow');
      root.style.removeProperty('--brand-shadow-strong');
      root.style.removeProperty('--brand-focus');
    };
  }, [activeTheme, isDark]);

  const setSeasonalTheme = useCallback(async (themeId) => {
    try {
      await firestoreService.set('appSettings', 'seasonalTheme', {
        themeId: themeId || null,
        updatedAt: new Date().toISOString(),
      });
      setActiveThemeId(themeId || null);
    } catch (err) {
      console.error('Error saving seasonal theme:', err);
      throw err;
    }
  }, []);

  const setThemeAvatar = useCallback(async (themeId, url) => {
    const updated = { ...avatars, [themeId]: url || null };
    if (!url) delete updated[themeId];
    try {
      await firestoreService.set('appSettings', 'seasonalTheme', {
        avatars: updated,
        updatedAt: new Date().toISOString(),
      });
      setAvatars(updated);
    } catch (err) {
      console.error('Error saving theme avatar:', err);
      throw err;
    }
  }, [avatars]);

  const setBannerTextValue = useCallback(async (text) => {
    try {
      await firestoreService.set('appSettings', 'seasonalTheme', {
        bannerText: text,
        updatedAt: new Date().toISOString(),
      });
      setBannerText(text);
    } catch (err) {
      console.error('Error saving banner text:', err);
      throw err;
    }
  }, []);

  const value = {
    activeThemeId,
    activeTheme,
    avatarUrl,
    avatars,
    bannerText,
    setSeasonalTheme,
    setThemeAvatar,
    setBannerTextValue,
    loading,
  };

  return (
    <SeasonalThemeContext.Provider value={value}>
      {children}
    </SeasonalThemeContext.Provider>
  );
};
